// components/ErrorBoundary.tsx
'use client'
import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

// notFound() and redirect() work by throwing an error whose digest Next.js recognises. A boundary that
// swallows those turns a 404 into "Something went wrong" (seen on mistyped /article/ URLs), so pass
// them on to Next's own not-found / redirect handling.
const isNextNavigationError = (error?: Error) => {
  const digest = (error as { digest?: unknown } | undefined)?.digest;
  return typeof digest === 'string' && /^(NEXT_HTTP_ERROR_FALLBACK|NEXT_NOT_FOUND|NEXT_REDIRECT)/.test(digest);
};

const ErrorFallback = ({ error }: { error?: Error }) => (
  <div className="text-red-500 p-4">
    <h2>Something went wrong</h2>
    {error && <pre>{error.message}</pre>}
  </div>
);

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (isNextNavigationError(error)) return;
    console.error('Uncaught error:', error, errorInfo);
  }

  public render() {
    if (this.state.hasError && isNextNavigationError(this.state.error)) {
      throw this.state.error;
    }
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
